"use client";

import { c as _c } from "react/compiler-runtime";
import { useBreakpoint } from "@dc/hooks";
import { cn } from "@dc/ui";
import { Avatar, Button, Divider, EllipsisTooltip, Input, Listbox, ListboxItem, Popover, PopoverContent, PopoverTrigger, ScrollShadow, Spinner, Tooltip } from "@dc/ui/components";
import type { Selection } from "@heroui/react";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import type { FC, ReactNode } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AltArrowDownLinear, ClockCircleLinear, Logout3Linear, MagniferLinear, QuestionCircleLinear, SettingsLinear, UserLinear } from "solar-icon-set";
import { Can } from "@/components/Ability/Can";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { useMe } from "@/hooks/useMe";
import { useNavigationUpdatesEnabled } from "@/hooks/useNavigationUpdatesEnabled";
import { usePylon } from "@/hooks/usePylon";
import { useSettingsEntry } from "@/hooks/useSettingsEntry";
import { getAvatarSrc } from "@/lib/avatarSrc";
import { getRelationshipScopeId } from "@/lib/getRelationshipScopeId";
import { useOrgTeamPath } from "@/lib/orgTeamPath";
import { ELEMENT_TYPE, trackElement } from "@/lib/tracking/analytics";
import { InternalToolsSection } from "./components/InternalToolsSection";
import { MenuRow } from "./components/MenuRow";
import { MenuSectionLabel } from "./components/MenuSectionLabel";
import { UserSettingsSection } from "./components/UserSettingsSection";
import { useOrgSwitcher } from "./hooks/useOrgSwitcher/useOrgSwitcher";
export interface OrgSwitcherProps {
  trigger?: ReactNode;
  isCollapsed?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  /** Organization id -> disabled reason. Matching orgs render disabled with the reason shown inline. Overrides the switcher's own entitlement-derived map when passed. */
  disabledOrganizations?: Record<string, string>;
  /** Called with the new org's id right after a selection updates context. The everyday sidebar usage doesn't need this — a permanently-mounted TeamSwitcher reacts to the context change itself and navigates. Callers that render OrgSwitcher outside that tree (no TeamSwitcher to react) must handle navigation themselves via this callback. */
  onOrganizationSelected?: (organizationId: string) => void;
  /** Adds Settings and Help to the legacy menu, for screens without the navigation sidebar that carries them. */
  showSettingsAndHelp?: boolean;
}
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const SCROLL_THRESHOLD = 100;
export const OrgSwitcher: FC<OrgSwitcherProps> = t0 => {
  const $ = _c(202);
  const {
    trigger,
    isCollapsed: t1,
    onOpenChange,
    disabledOrganizations: disabledOrganizationsProp,
    onOrganizationSelected,
    showSettingsAndHelp: t2
  } = t0;
  const isCollapsed = t1 === undefined ? false : t1;
  const showSettingsAndHelp = t2 === undefined ? false : t2;
  const [isOpen, setIsOpen] = useState(false);
  const scrollRef = useRef(null);
  const {
    me,
    userId: myUserId
  } = useMe();
  const isDesktop = useBreakpoint("md") ?? true;
  const t = useTranslations("navigation.org_switcher");
  const tActions = useTranslations("navigation.actions");
  const navigationUpdatesEnabled = useNavigationUpdatesEnabled();
  const {
    toggleChat
  } = usePylon();
  const router = useRouter();
  const {
    build
  } = useOrgTeamPath();
  const {
    "pd-6-user-profile": userProfileEnabled,
    "trials-enable-provisioning-pd-6": trialsEnabled
  } = useFeatureFlags();
  const {
    organizations,
    currentOrganization,
    currentOrganizationId,
    totalCount,
    hasMore,
    isPending,
    isFetchingMore,
    showSearch,
    searchValue,
    setSearchValue,
    loadMore,
    selectOrganization,
    handleLogout,
    isLoggingOut,
    disabledOrganizations: fetchedDisabledOrganizations
  } = useOrgSwitcher();
  const disabledOrganizations = disabledOrganizationsProp ?? fetchedDisabledOrganizations;
  let t3;
  if ($[0] !== currentOrganization?.relationships?.scope?.data) {
    t3 = getRelationshipScopeId(currentOrganization?.relationships?.scope?.data) || undefined;
    $[0] = currentOrganization?.relationships?.scope?.data;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const settingsEntry = useSettingsEntry(t3);
  const currentOrgName = currentOrganization?.attributes?.name ?? null;
  const hasMultipleOrgs = totalCount > 1;
  let t4;
  if ($[2] !== onOpenChange) {
    t4 = open => {
      setIsOpen(open);
      onOpenChange?.(open);
    };
    $[2] = onOpenChange;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const handleOpenChange = t4;
  let t5;
  if ($[4] !== build || $[5] !== handleOpenChange || $[6] !== router) {
    t5 = logicalPath => {
      router.push(build(logicalPath));
      handleOpenChange(false);
    };
    $[4] = build;
    $[5] = handleOpenChange;
    $[6] = router;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const navigateTo = t5;
  let t6;
  let t7;
  if ($[8] !== isOpen) {
    t6 = () => {
      if (isOpen) {
        document.documentElement.dataset.overlayOpen = "true";
      } else {
        delete document.documentElement.dataset.overlayOpen;
      }
      return _temp;
    };
    t7 = [isOpen];
    $[8] = isOpen;
    $[9] = t6;
    $[10] = t7;
  } else {
    t6 = $[9];
    t7 = $[10];
  }
  useEffect(t6, t7);
  let t8;
  if ($[11] !== hasMore || $[12] !== isFetchingMore || $[13] !== loadMore) {
    t8 = e => {
      const element = e.currentTarget;
      const isNearBottom = element.scrollHeight - element.scrollTop - element.clientHeight < SCROLL_THRESHOLD;
      if (isNearBottom && hasMore && !isFetchingMore) {
        loadMore();
      }
    };
    $[11] = hasMore;
    $[12] = isFetchingMore;
    $[13] = loadMore;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const handleScroll = t8;
  let t9;
  if ($[15] !== currentOrganizationId || $[16] !== handleOpenChange || $[17] !== onOrganizationSelected || $[18] !== organizations || $[19] !== selectOrganization) {
    t9 = keys => {
      if (keys === "all") {
        return;
      }
      const selectedKey = Array.from(keys)[0];
      if (selectedKey && String(selectedKey) !== currentOrganizationId) {
        const selectedOrg = organizations.find(org => org.id === String(selectedKey));
        if (selectedOrg) {
          selectOrganization(selectedOrg);
          onOrganizationSelected?.(selectedOrg.id);
          handleOpenChange(false);
        }
      }
    };
    $[15] = currentOrganizationId;
    $[16] = handleOpenChange;
    $[17] = onOrganizationSelected;
    $[18] = organizations;
    $[19] = selectOrganization;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  const handleSelectionChange = t9;
  me?.data?.attributes?.firstName;
  me?.data?.attributes?.lastName;
  let t10;
  if ($[21] !== me?.data?.attributes?.firstName) {
    t10 = me?.data?.attributes?.firstName?.trim();
    $[21] = me?.data?.attributes?.firstName;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  const firstName = t10;
  let t11;
  if ($[23] !== me?.data?.attributes?.lastName) {
    t11 = me?.data?.attributes?.lastName?.trim();
    $[23] = me?.data?.attributes?.lastName;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  const lastName = t11;
  let t12;
  if ($[25] !== firstName || $[26] !== lastName) {
    t12 = [firstName, lastName].filter(Boolean);
    $[25] = firstName;
    $[26] = lastName;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  const nameParts = t12 as string[];
  const displayName = nameParts.join(" ") || "User";
  let t13;
  if ($[28] !== displayName) {
    t13 = UUID_REGEX.test(displayName);
    $[28] = displayName;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  const isUUID = t13;
  let t14;
  if ($[30] !== displayName || $[31] !== isUUID) {
    t14 = {
      displayName,
      isUUID
    };
    $[30] = displayName;
    $[31] = isUUID;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  const userData = t14;
  const t15 = !userData.isUUID ? userData.displayName : undefined;
  let t16;
  if ($[33] !== myUserId) {
    t16 = getAvatarSrc(myUserId);
    $[33] = myUserId;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== t15 || $[36] !== t16 || $[37] !== userData.displayName) {
    t17 = <Avatar name={t15} src={t16} className="size-6" aria-label={userData.displayName} />;
    $[35] = t15;
    $[36] = t16;
    $[37] = userData.displayName;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  const avatarElement = t17;
  const t18 = userData.displayName;
  let t19;
  if ($[39] !== t) {
    t19 = t("account_and_organization_menu");
    $[39] = t;
    $[40] = t19;
  } else {
    t19 = $[40];
  }
  let t20;
  if ($[41] !== currentOrgName || $[42] !== t19 || $[43] !== userData.displayName) {
    t20 = [t18, currentOrgName, t19].filter(Boolean);
    $[41] = currentOrgName;
    $[42] = t19;
    $[43] = userData.displayName;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  const triggerLabel = t20.join(", ");
  const avatarOnly = isCollapsed || navigationUpdatesEnabled;
  const t21 = avatarOnly ? "justify-center" : "justify-start";
  let t22;
  if ($[45] !== t21) {
    t22 = cn("h-auto bg-transparent! py-2 text-zinc-300! transition-all hover:bg-navy-700! hover:text-zinc-300!", t21);
    $[45] = t21;
    $[46] = t22;
  } else {
    t22 = $[46];
  }
  const t23 = !avatarOnly;
  let t24;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = trackElement(ELEMENT_TYPE.Button, "User Menu");
    $[47] = t24;
  } else {
    t24 = $[47];
  }
  let t25;
  if ($[48] !== avatarElement || $[49] !== avatarOnly || $[50] !== currentOrgName || $[51] !== hasMultipleOrgs || $[52] !== userData.displayName) {
    t25 = avatarOnly ? avatarElement : <div className="flex w-full items-center gap-2"><div className="shrink-0">{avatarElement}</div><div className="flex min-w-0 flex-1 flex-col items-start"><EllipsisTooltip as="span" content={userData.displayName} className="w-full text-left text-sm">{userData.displayName}</EllipsisTooltip>{currentOrgName && <span data-testid="org-switcher-current-org" className="contents"><EllipsisTooltip as="span" content={currentOrgName} className="contents w-full text-left text-xs leading-0">{currentOrgName}</EllipsisTooltip></span>}</div>{hasMultipleOrgs && <AltArrowDownLinear className="size-4 shrink-0 text-default-400" />}</div>;
    $[48] = avatarElement;
    $[49] = avatarOnly;
    $[50] = currentOrgName;
    $[51] = hasMultipleOrgs;
    $[52] = userData.displayName;
    $[53] = t25;
  } else {
    t25 = $[53];
  }
  let t26;
  if ($[54] !== avatarOnly || $[55] !== t22 || $[56] !== t23 || $[57] !== t25 || $[58] !== triggerLabel) {
    t26 = <Button className={t22} isIconOnly={avatarOnly} fullWidth={t23} aria-label={triggerLabel} {...t24}>{t25}</Button>;
    $[54] = avatarOnly;
    $[55] = t22;
    $[56] = t23;
    $[57] = t25;
    $[58] = triggerLabel;
    $[59] = t26;
  } else {
    t26 = $[59];
  }
  const defaultTrigger = t26;
  const showOnlyLogout = !isPending && organizations.length <= 1 && !searchValue;
  let t27;
  if ($[60] !== currentOrganizationId || $[61] !== disabledOrganizations || $[62] !== handleSelectionChange || $[63] !== isPending || $[64] !== organizations || $[65] !== t) {
    t27 = isPending ? <div className="flex items-center justify-center py-8"><Spinner size="sm" /></div> : organizations.length === 0 ? <div className="py-8 text-center text-default-500 text-sm">{t("no_organizations_found")}</div> : <Listbox aria-label={t("organizations")} selectionMode="single" selectedKeys={currentOrganizationId ? new Set([currentOrganizationId]) : new Set()} disabledKeys={new Set(Object.keys(disabledOrganizations))} onSelectionChange={handleSelectionChange} classNames={{
      list: "gap-0"
    }}>{organizations.map(org_0 => {
        const orgName = org_0.attributes?.name ?? "Unnamed Organization";
        const disabledReason = disabledOrganizations[org_0.id];
        return <ListboxItem key={org_0.id} textValue={orgName} description={disabledReason} classNames={{
          title: "max-w-[180px]"
        }} {...trackElement(ELEMENT_TYPE.DropdownItem, "Switch Organization", {
          org_name: orgName
        })}><EllipsisTooltip content={orgName} className="max-w-[180px]" placement="right" color="foreground">{orgName}</EllipsisTooltip></ListboxItem>;
      })}</Listbox>;
    $[60] = currentOrganizationId;
    $[61] = disabledOrganizations;
    $[62] = handleSelectionChange;
    $[63] = isPending;
    $[64] = organizations;
    $[65] = t;
    $[66] = t27;
  } else {
    t27 = $[66];
  }
  let t28;
  if ($[67] !== isFetchingMore) {
    t28 = isFetchingMore && <div className="flex items-center justify-center py-2"><Spinner size="sm" /></div>;
    $[67] = isFetchingMore;
    $[68] = t28;
  } else {
    t28 = $[68];
  }
  let t29;
  if ($[69] !== t27 || $[70] !== t28) {
    t29 = <>{t27}{t28}</>;
    $[69] = t27;
    $[70] = t28;
    $[71] = t29;
  } else {
    t29 = $[71];
  }
  const organizationList = t29;
  let t30;
  if ($[72] !== handleScroll || $[73] !== organizationList || $[74] !== searchValue || $[75] !== setSearchValue || $[76] !== showOnlyLogout || $[77] !== showSearch || $[78] !== t) {
    t30 = !showOnlyLogout && <>{showSearch && <div className="p-2 py-3"><Input placeholder={t("search_organizations")} value={searchValue} onValueChange={setSearchValue} startContent={<MagniferLinear className="text-default-400" />} size="sm" classNames={{
          inputWrapper: "bg-default-100",
          input: "text-base sm:text-sm"
        }} aria-label={t("search_organizations")} /></div>}<ScrollShadow ref={scrollRef} className="max-h-64" onScroll={handleScroll}>{organizationList}</ScrollShadow></>;
    $[72] = handleScroll;
    $[73] = organizationList;
    $[74] = searchValue;
    $[75] = setSearchValue;
    $[76] = showOnlyLogout;
    $[77] = showSearch;
    $[78] = t;
    $[79] = t30;
  } else {
    t30 = $[79];
  }
  const t31 = !showOnlyLogout && "border-border-divider-neutral-usual border-t";
  let t32;
  if ($[80] !== t31) {
    t32 = cn("p-2", t31);
    $[80] = t31;
    $[81] = t32;
  } else {
    t32 = $[81];
  }
  let t33;
  if ($[82] !== build || $[83] !== handleOpenChange || $[84] !== myUserId || $[85] !== router || $[86] !== t || $[87] !== userProfileEnabled) {
    t33 = userProfileEnabled && <Button fullWidth={true} variant="light" color="default" className="justify-start" startContent={<UserLinear className="size-5" />} onPress={() => {
      const path = myUserId ? `/profile/${myUserId}` : "/profile";
      router.push(build(path));
      handleOpenChange(false);
    }} aria-label={t("my_profile")} {...trackElement(ELEMENT_TYPE.Button, "My Profile")}>{t("my_profile")}</Button>;
    $[82] = build;
    $[83] = handleOpenChange;
    $[84] = myUserId;
    $[85] = router;
    $[86] = t;
    $[87] = userProfileEnabled;
    $[88] = t33;
  } else {
    t33 = $[88];
  }
  let t34;
  if ($[89] !== build || $[90] !== handleOpenChange || $[91] !== router || $[92] !== settingsEntry || $[93] !== showSettingsAndHelp || $[94] !== tActions) {
    t34 = showSettingsAndHelp && settingsEntry && <Button fullWidth={true} variant="light" color="default" className="justify-start" startContent={<SettingsLinear className="size-5" />} onPress={() => {
      router.push(build(settingsEntry.href));
      handleOpenChange(false);
    }} {...trackElement(ELEMENT_TYPE.Button, settingsEntry.kind === "org" ? "Organization Settings" : "Team Settings")}>{settingsEntry.kind === "org" ? tActions("settings") : tActions("team_settings")}</Button>;
    $[89] = build;
    $[90] = handleOpenChange;
    $[91] = router;
    $[92] = settingsEntry;
    $[93] = showSettingsAndHelp;
    $[94] = tActions;
    $[95] = t34;
  } else {
    t34 = $[95];
  }
  let t35;
  if ($[96] !== build || $[97] !== handleOpenChange || $[98] !== router || $[99] !== t || $[100] !== trialsEnabled) {
    t35 = trialsEnabled && <Can action="read" resource="TRIAL"><Button fullWidth={true} variant="light" color="default" className="justify-start" startContent={<ClockCircleLinear className="size-5" />} onPress={() => {
        router.push(build("/trials"));
        handleOpenChange(false);
      }} aria-label={t("trials")} {...trackElement(ELEMENT_TYPE.Button, "Trials")}>{t("trials")}</Button></Can>;
    $[96] = build;
    $[97] = handleOpenChange;
    $[98] = router;
    $[99] = t;
    $[100] = trialsEnabled;
    $[101] = t35;
  } else {
    t35 = $[101];
  }
  let t36;
  if ($[102] !== handleOpenChange || $[103] !== showSettingsAndHelp || $[104] !== tActions || $[105] !== toggleChat) {
    t36 = showSettingsAndHelp && <Button fullWidth={true} variant="light" color="default" className="justify-start" startContent={<QuestionCircleLinear className="size-5" />} onPress={() => {
      toggleChat();
      handleOpenChange(false);
    }} {...trackElement(ELEMENT_TYPE.Button, "Help and Resources")}>{tActions("help_and_resources")}</Button>;
    $[102] = handleOpenChange;
    $[103] = showSettingsAndHelp;
    $[104] = tActions;
    $[105] = toggleChat;
    $[106] = t36;
  } else {
    t36 = $[106];
  }
  let t37;
  if ($[107] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = <Logout3Linear className="size-5" />;
    $[107] = t37;
  } else {
    t37 = $[107];
  }
  let t38;
  if ($[108] !== t) {
    t38 = t("logout");
    $[108] = t;
    $[109] = t38;
  } else {
    t38 = $[109];
  }
  let t39;
  if ($[110] === Symbol.for("react.memo_cache_sentinel")) {
    t39 = trackElement(ELEMENT_TYPE.Button, "Log Out");
    $[110] = t39;
  } else {
    t39 = $[110];
  }
  let t40;
  if ($[111] !== t) {
    t40 = t("logout");
    $[111] = t;
    $[112] = t40;
  } else {
    t40 = $[112];
  }
  let t41;
  if ($[113] !== handleLogout || $[114] !== isLoggingOut || $[115] !== t38 || $[116] !== t40) {
    t41 = <Button fullWidth={true} variant="light" color="default" className="justify-start" startContent={t37} onPress={handleLogout} isLoading={isLoggingOut} aria-label={t38} {...t39}>{t40}</Button>;
    $[113] = handleLogout;
    $[114] = isLoggingOut;
    $[115] = t38;
    $[116] = t40;
    $[117] = t41;
  } else {
    t41 = $[117];
  }
  let t42;
  if ($[118] !== t32 || $[119] !== t33 || $[120] !== t34 || $[121] !== t35 || $[122] !== t36 || $[123] !== t41) {
    t42 = <div className={t32}>{t33}{t34}{t35}{t36}{t41}</div>;
    $[118] = t32;
    $[119] = t33;
    $[120] = t34;
    $[121] = t35;
    $[122] = t36;
    $[123] = t41;
    $[124] = t42;
  } else {
    t42 = $[124];
  }
  let t43;
  if ($[125] !== t30 || $[126] !== t42) {
    t43 = <PopoverContent className="max-w-72 p-0"><div className="flex flex-col">{t30}{t42}</div></PopoverContent>;
    $[125] = t30;
    $[126] = t42;
    $[127] = t43;
  } else {
    t43 = $[127];
  }
  const legacyPopoverContent = t43;
  const t44 = !userData.isUUID ? userData.displayName : undefined;
  let t45;
  if ($[128] !== myUserId) {
    t45 = getAvatarSrc(myUserId);
    $[128] = myUserId;
    $[129] = t45;
  } else {
    t45 = $[129];
  }
  let t46;
  if ($[130] !== t44 || $[131] !== t45) {
    t46 = <Avatar name={t44} src={t45} className="size-10 shrink-0" />;
    $[130] = t44;
    $[131] = t45;
    $[132] = t46;
  } else {
    t46 = $[132];
  }
  let t47;
  if ($[133] !== userData.displayName) {
    t47 = <EllipsisTooltip as="span" content={userData.displayName} className="w-full text-left font-medium text-sm">{userData.displayName}</EllipsisTooltip>;
    $[133] = userData.displayName;
    $[134] = t47;
  } else {
    t47 = $[134];
  }
  let t48;
  if ($[135] !== currentOrgName) {
    t48 = currentOrgName && <span data-testid="org-switcher-current-org" className="contents"><EllipsisTooltip as="span" content={currentOrgName} className="w-full text-left text-text-interactive-neutral-faint text-xs">{currentOrgName}</EllipsisTooltip></span>;
    $[135] = currentOrgName;
    $[136] = t48;
  } else {
    t48 = $[136];
  }
  let t49;
  if ($[137] !== t47 || $[138] !== t48) {
    t49 = <div className="flex min-w-0 flex-col">{t47}{t48}</div>;
    $[137] = t47;
    $[138] = t48;
    $[139] = t49;
  } else {
    t49 = $[139];
  }
  let t50;
  if ($[140] !== t46 || $[141] !== t49) {
    t50 = <div className="flex w-full items-center gap-3 px-4 py-3">{t46}{t49}</div>;
    $[140] = t46;
    $[141] = t49;
    $[142] = t50;
  } else {
    t50 = $[142];
  }
  let t51;
  if ($[143] !== handleScroll || $[144] !== organizationList || $[145] !== searchValue || $[146] !== setSearchValue || $[147] !== showOnlyLogout || $[148] !== showSearch || $[149] !== t) {
    t51 = !showOnlyLogout && <><Divider /><div className="flex w-full flex-col gap-1 p-2"><MenuSectionLabel>{t("organization")}</MenuSectionLabel>{showSearch && <Input placeholder={t("search_organizations")} value={searchValue} onValueChange={setSearchValue} startContent={<MagniferLinear className="text-default-400" />} size="sm" className="px-2" classNames={{
          inputWrapper: "bg-default-100",
          input: "text-base sm:text-sm"
        }} aria-label={t("search_organizations")} />}<ScrollShadow ref={scrollRef} className="max-h-48" onScroll={handleScroll}>{organizationList}</ScrollShadow></div></>;
    $[143] = handleScroll;
    $[144] = organizationList;
    $[145] = searchValue;
    $[146] = setSearchValue;
    $[147] = showOnlyLogout;
    $[148] = showSearch;
    $[149] = t;
    $[150] = t51;
  } else {
    t51 = $[150];
  }
  let t52;
  if ($[151] === Symbol.for("react.memo_cache_sentinel")) {
    t52 = <Divider />;
    $[151] = t52;
  } else {
    t52 = $[151];
  }
  let t53;
  if ($[152] === Symbol.for("react.memo_cache_sentinel")) {
    t53 = <QuestionCircleLinear className="size-5" />;
    $[152] = t53;
  } else {
    t53 = $[152];
  }
  let t54;
  if ($[153] !== tActions) {
    t54 = tActions("help_and_resources");
    $[153] = tActions;
    $[154] = t54;
  } else {
    t54 = $[154];
  }
  let t55;
  if ($[155] !== handleOpenChange || $[156] !== toggleChat) {
    t55 = () => {
      toggleChat();
      handleOpenChange(false);
    };
    $[155] = handleOpenChange;
    $[156] = toggleChat;
    $[157] = t55;
  } else {
    t55 = $[157];
  }
  let t56;
  if ($[158] !== t54 || $[159] !== t55) {
    t56 = <div className="flex w-full flex-col p-2"><MenuRow icon={t53} label={t54} onPress={t55} testId="sidebar-help" trackingLabel="Help and Resources" /></div>;
    $[158] = t54;
    $[159] = t55;
    $[160] = t56;
  } else {
    t56 = $[160];
  }
  let t57;
  if ($[161] === Symbol.for("react.memo_cache_sentinel")) {
    t57 = <Divider />;
    $[161] = t57;
  } else {
    t57 = $[161];
  }
  let t58;
  if ($[162] !== handleOpenChange) {
    t58 = () => handleOpenChange(false);
    $[162] = handleOpenChange;
    $[163] = t58;
  } else {
    t58 = $[163];
  }
  let t59;
  if ($[164] !== navigateTo || $[165] !== t58) {
    t59 = <UserSettingsSection onNavigate={navigateTo} onClose={t58} />;
    $[164] = navigateTo;
    $[165] = t58;
    $[166] = t59;
  } else {
    t59 = $[166];
  }
  let t60;
  if ($[167] !== navigateTo) {
    t60 = <InternalToolsSection onNavigate={navigateTo} />;
    $[167] = navigateTo;
    $[168] = t60;
  } else {
    t60 = $[168];
  }
  let t61;
  if ($[169] === Symbol.for("react.memo_cache_sentinel")) {
    t61 = <Divider />;
    $[169] = t61;
  } else {
    t61 = $[169];
  }
  let t62;
  if ($[170] === Symbol.for("react.memo_cache_sentinel")) {
    t62 = <Logout3Linear className="size-5" />;
    $[170] = t62;
  } else {
    t62 = $[170];
  }
  let t63;
  if ($[171] !== t) {
    t63 = t("logout");
    $[171] = t;
    $[172] = t63;
  } else {
    t63 = $[172];
  }
  let t64;
  if ($[173] !== handleLogout || $[174] !== isLoggingOut || $[175] !== t63) {
    t64 = <div className="flex w-full flex-col p-2"><MenuRow icon={t62} label={t63} onPress={handleLogout} isLoading={isLoggingOut} testId="sidebar-logout" trackingLabel="Log Out" /></div>;
    $[173] = handleLogout;
    $[174] = isLoggingOut;
    $[175] = t63;
    $[176] = t64;
  } else {
    t64 = $[176];
  }
  let t65;
  if ($[177] !== t50 || $[178] !== t51 || $[179] !== t56 || $[180] !== t59 || $[181] !== t60 || $[182] !== t64) {
    t65 = <PopoverContent className="w-80 max-w-[calc(100vw-5rem)] p-0"><div className="flex w-full flex-col">{t50}{t51}{t52}{t56}{t57}{t59}{t60}{t61}{t64}</div></PopoverContent>;
    $[177] = t50;
    $[178] = t51;
    $[179] = t56;
    $[180] = t59;
    $[181] = t60;
    $[182] = t64;
    $[183] = t65;
  } else {
    t65 = $[183];
  }
  const menuPopoverContent = t65;
  const popoverContent = navigationUpdatesEnabled ? menuPopoverContent : legacyPopoverContent;
  const t66 = isDesktop ? "right-start" : "top";
  let t67;
  if ($[184] !== isPending || $[185] !== organizations.length || $[186] !== searchValue) {
    t67 = [organizations.length, isPending, searchValue];
    $[184] = isPending;
    $[185] = organizations.length;
    $[186] = searchValue;
    $[187] = t67;
  } else {
    t67 = $[187];
  }
  const t68 = trigger ?? defaultTrigger;
  let t69;
  if ($[188] !== t68) {
    t69 = <PopoverTrigger>{t68}</PopoverTrigger>;
    $[188] = t68;
    $[189] = t69;
  } else {
    t69 = $[189];
  }
  let t70;
  if ($[190] !== handleOpenChange || $[191] !== isOpen || $[192] !== popoverContent || $[193] !== t66 || $[194] !== t67 || $[195] !== t69) {
    t70 = <Popover isOpen={isOpen} onOpenChange={handleOpenChange} placement={t66} offset={8} shouldCloseOnScroll={false} updatePositionDeps={t67}>{t69}{popoverContent}</Popover>;
    $[190] = handleOpenChange;
    $[191] = isOpen;
    $[192] = popoverContent;
    $[193] = t66;
    $[194] = t67;
    $[195] = t69;
    $[196] = t70;
  } else {
    t70 = $[196];
  }
  const popover = t70;
  if (avatarOnly && !trigger) {
    let t71;
    if ($[197] !== popover) {
      t71 = <div className="w-full">{popover}</div>;
      $[197] = popover;
      $[198] = t71;
    } else {
      t71 = $[198];
    }
    let t72;
    if ($[199] !== t71 || $[200] !== userData.displayName) {
      t72 = <Tooltip content={userData.displayName} placement="right" color="foreground">{t71}</Tooltip>;
      $[199] = t71;
      $[200] = userData.displayName;
      $[201] = t72;
    } else {
      t72 = $[201];
    }
    return t72;
  }
  return popover;
};
function _temp() {
  delete document.documentElement.dataset.overlayOpen;
}